import type { TcpChannel } from '@casa/bridge';
import { monotonicNow, wallNow } from '@casa/util';
import type { DecoderStats } from '../h264/h264_decoder.ts';
import type { DepacketizerStats } from '../h264/h264_depacketizer.ts';
import { RtspClient } from '../rtsp/rtsp_client.ts';
import { parseRtspUrl } from '../rtsp/rtsp_url.ts';
import { createVideoPipeline, type PipelineMode, type VideoPipeline, type VideoPipelineOptions } from './video_pipeline.ts';

/** `waiting`: between a lost or failed connection and the next attempt. */
export type StreamState = 'connecting' | 'playing' | 'waiting' | 'stopped';

export interface StreamEvent {
    /** `wallNow()`. */
    time: number;
    type: 'connecting' | 'playing' | 'first-picture' | 'stalled' | 'error' | 'closed' | 'stopped';
    detail?: string;
}

export interface StreamStats {
    state: StreamState;
    /** Where the pipeline runs; null until that is known. */
    mode: PipelineMode | null;
    modeFallbackReason: string | null;
    /** Time since `start`. */
    uptimeMs: number;
    /** Connections that got as far as PLAY. */
    sessions: number;
    /** Times the watchdog ended a session because no picture was decoded for the stall timeout. */
    stalls: number;
    /** Connection attempts that failed and sessions that the camera or the network ended. */
    failures: number;
    lastError: string | null;
    /** Pictures decoded in all sessions. */
    framesDecodedTotal: number;
    /** The current session, or the last one while there is none. */
    session: {
        /** Time since PLAY was answered. */
        playingMs: number;
        /** Time from PLAY to the first decoded picture; null until then. */
        firstFrameMs: number | null;
        /** RTP bytes of the video track, headers included. */
        rtpBytes: number;
        track: { payloadType: number; clockRate: number; packetizationMode: number; profileLevelId: string | null };
    } | null;
    depacketizer: DepacketizerStats | null;
    decoder: DecoderStats | null;
    /** The most recent events, oldest first. */
    events: StreamEvent[];
}

export interface RtspStreamOptions {
    url: string;
    /** Opens a connection to the camera; called again for every reconnect. */
    openChannel(host: string, port: number) : Promise<TcpChannel>;
    /** With a Worker pipeline the canvas is handed over for good; nothing else may draw on it. */
    canvas: HTMLCanvasElement;
    mode?: PipelineMode | 'auto';
    /** No decoded picture for this long ends the session and reconnects. Default 30 s. */
    stallTimeoutMs?: number;
    /** Wait before the first retry; doubled for each further one, up to the maximum. Default 2 s and 15 s. */
    retryDelayMs?: number;
    maxRetryDelayMs?: number;
    log?: (message: string) => void;
    /** For tests. */
    createPipeline?: (canvas: HTMLCanvasElement, options: VideoPipelineOptions) => Promise<VideoPipeline>;
}

const WATCHDOG_INTERVAL_MS = 500;
const MAX_EVENTS = 50;

const text = (error: unknown) : string => (error instanceof Error ? error.message : String(error));

/**
 * One camera stream on a canvas that keeps itself alive: RTSP client on the page, depacketiser
 * and decoder in the pipeline (a Worker where the engine allows), and a watchdog that reconnects
 * when the connection is lost or no picture has been decoded for the stall timeout.
 */
export class RtspStream {
    readonly #options: RtspStreamOptions;
    readonly #stallTimeoutMs: number;
    readonly #events: StreamEvent[] = [];
    #state: StreamState = 'stopped';
    #pipeline: VideoPipeline | null = null;
    #client: RtspClient | null = null;
    // Raised whenever the current connection attempt or session is over; late callbacks compare it.
    #generation = 0;
    #startedAt = 0;
    #watchdog: ReturnType<typeof setInterval> | undefined;
    #retryTimer: ReturnType<typeof setTimeout> | undefined;
    #firstOutcome: (() => void) | null = null;
    #session: { startedAt: number; rtpBytes: number; track: NonNullable<StreamStats['session']>['track'] } | null = null;
    #sessionActive = false;
    #sessions = 0;
    #stalls = 0;
    #failures = 0;
    #lastError: string | null = null;
    #framesBefore = 0;
    #framesNow = 0;
    #lastProgressAt = 0;
    // Losses since the last decoded picture; sets the wait before the next attempt.
    #retries = 0;

    constructor(options: RtspStreamOptions) {
        this.#options = options;
        this.#stallTimeoutMs = options.stallTimeoutMs ?? 30_000;
    }

    /** Starts playing. Resolves when the first attempt has either reached PLAY or failed. */
    async start() : Promise<void> {
        if (this.#state !== 'stopped') {
            return;
        }

        this.#state = 'connecting';
        this.#startedAt = wallNow();
        const generation = ++this.#generation;
        const { mode, log } = this.#options;
        let pipeline: VideoPipeline;
        try {
            pipeline = await (this.#options.createPipeline ?? createVideoPipeline)(this.#options.canvas, {
                ...(mode ? { mode } : {}),
                ...(log ? { log } : {}),
            });
        } catch (error) {
            if (generation === this.#generation) {
                this.#state = 'stopped';
            }
            throw error;
        }
        if (generation !== this.#generation) {
            pipeline.close();

            return;
        }
        this.#pipeline = pipeline;
        this.#watchdog = setInterval(() => this.#watch(), WATCHDOG_INTERVAL_MS);
        const outcome = new Promise<void>((resolve) => {
            this.#firstOutcome = resolve;
        });
        void this.#connect();
        await outcome;
    }

    async stop() : Promise<void> {
        if (this.#state === 'stopped') {
            return;
        }

        this.#generation++;
        clearInterval(this.#watchdog);
        clearTimeout(this.#retryTimer);
        this.#endSession();
        this.#state = 'stopped';
        this.#event('stopped');
        this.#settleFirstOutcome();
        const client = this.#client;
        this.#client = null;
        this.#pipeline?.close();
        await client?.stop();
    }

    stats() : StreamStats {
        const pipelineStats = this.#pipeline?.stats() ?? null;
        const firstFrameTime = pipelineStats?.decoder.firstFrameTime ?? null;
        // Read here as well: the watchdog's count is as old as its last tick, and ticks are rare in a hidden page.
        const framesNow = this.#sessionActive ? Math.max(this.#framesNow, pipelineStats?.decoder.framesDecoded ?? 0) : 0;

        return {
            state: this.#state,
            mode: this.#pipeline?.mode ?? null,
            modeFallbackReason: this.#pipeline?.fallbackReason ?? null,
            uptimeMs: this.#startedAt === 0 ? 0 : wallNow() - this.#startedAt,
            sessions: this.#sessions,
            stalls: this.#stalls,
            failures: this.#failures,
            lastError: this.#lastError,
            framesDecodedTotal: this.#framesBefore + framesNow,
            session: this.#session && {
                playingMs: wallNow() - this.#session.startedAt,
                firstFrameMs: firstFrameTime === null ? null : firstFrameTime - this.#session.startedAt,
                rtpBytes: this.#session.rtpBytes,
                track: this.#session.track,
            },
            depacketizer: pipelineStats?.depacketizer ?? null,
            decoder: pipelineStats?.decoder ?? null,
            events: [...this.#events],
        };
    }

    #event(type: StreamEvent['type'], detail?: string) : void {
        this.#events.push({ time: wallNow(), type, ...(detail === undefined ? {} : { detail }) });
        if (this.#events.length > MAX_EVENTS) {
            this.#events.shift();
        }
        this.#options.log?.(`video: ${type}${detail === undefined ? '' : ` (${detail})`}`);
    }

    #settleFirstOutcome() : void {
        this.#firstOutcome?.();
        this.#firstOutcome = null;
    }

    async #connect() : Promise<void> {
        const pipeline = this.#pipeline!;
        const generation = ++this.#generation;
        const current = () : boolean => generation === this.#generation;
        this.#state = 'connecting';
        this.#event('connecting');

        let client: RtspClient | undefined;
        try {
            const target = parseRtspUrl(this.#options.url);
            const channel = await this.#options.openChannel(target.host, target.port);
            client = new RtspClient(channel, this.#options.url, this.#options.log ? { log: this.#options.log } : {});
            if (!current()) {
                void client.stop();

                return;
            }
            this.#client = client;

            // Packets are handed on once per task, so that all views on one transport chunk travel
            // together: a Worker pipeline takes the chunk's buffer away from this thread.
            let live = false;
            let rtpBytes = 0;
            let batch: Uint8Array[] = [];
            const flush = () : void => {
                if (!live || batch.length === 0 || !current()) {
                    return;
                }

                const packets = batch;
                batch = [];
                pipeline.push(packets, wallNow());
            };
            client.onRtp((packet) => {
                if (this.#session && live) {
                    this.#session.rtpBytes += packet.length;
                } else {
                    rtpBytes += packet.length;
                }
                if (batch.length === 0) {
                    queueMicrotask(flush);
                }
                batch.push(packet);
            });

            const { track } = await client.start();
            if (!current()) {
                void client.stop();

                return;
            }
            pipeline.start({ payloadType: track.payloadType, clockRate: track.clockRate, sps: track.sps, pps: track.pps });
            this.#session = {
                startedAt: wallNow(),
                rtpBytes,
                track: {
                    payloadType: track.payloadType,
                    clockRate: track.clockRate,
                    packetizationMode: track.packetizationMode,
                    profileLevelId: track.profileLevelId ?? null,
                },
            };
            this.#sessionActive = true;
            this.#framesNow = 0;
            this.#lastProgressAt = monotonicNow();
            this.#sessions++;
            this.#state = 'playing';
            this.#event('playing');
            client.onClose((reason) => {
                if (current()) {
                    this.#lose('closed', reason ?? 'connection closed');
                }
            });
            // Packets that came in with the PLAY answer.
            live = true;
            flush();
            this.#settleFirstOutcome();
        } catch (error) {
            if (current()) {
                this.#lose('error', text(error));
            } else {
                void client?.stop();
            }
        }
    }

    #watch() : void {
        if (this.#state !== 'playing') {
            return;
        }

        const frames = this.#pipeline?.stats()?.decoder.framesDecoded ?? 0;
        const now = monotonicNow();
        if (frames > this.#framesNow) {
            if (this.#framesNow === 0) {
                this.#event('first-picture');
            }
            this.#framesNow = frames;
            this.#lastProgressAt = now;
            this.#retries = 0;
        } else if (now - this.#lastProgressAt >= this.#stallTimeoutMs) {
            this.#lose('stalled', `no picture for ${Math.round(this.#stallTimeoutMs / 1000)} s`);
        }
    }

    /** The attempt or session is over: clean up and schedule the next attempt. */
    #lose(type: 'stalled' | 'error' | 'closed', detail: string) : void {
        this.#generation++;
        if (type === 'stalled') {
            this.#stalls++;
        } else {
            this.#failures++;
        }
        this.#lastError = detail;
        this.#event(type, detail);
        this.#endSession();
        const client = this.#client;
        this.#client = null;
        // Not awaited: on a dead connection the TEARDOWN and the close can take their time.
        void client?.stop();

        const base = this.#options.retryDelayMs ?? 2000;
        // A stalled stream is reconnected at once; anything else, and every repeat, waits.
        const delay =
            type === 'stalled' && this.#retries === 0
                ? 0
                : Math.min(base * 2 ** this.#retries, this.#options.maxRetryDelayMs ?? 15_000);
        this.#retries++;
        this.#state = 'waiting';
        this.#retryTimer = setTimeout(() => void this.#connect(), delay);
        this.#settleFirstOutcome();
    }

    #endSession() : void {
        if (!this.#sessionActive) {
            return;
        }

        this.#sessionActive = false;
        // The watchdog's count can be half a second old.
        this.#framesNow = Math.max(this.#framesNow, this.#pipeline?.stats()?.decoder.framesDecoded ?? 0);
        this.#framesBefore += this.#framesNow;
        this.#framesNow = 0;
        // The canvas keeps the last picture until the next session paints over it.
        this.#pipeline?.end();
    }
}
